import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { json } from "@remix-run/node";
import {
  Form,
  Link,
  useActionData,
  useNavigation,
  useOutletContext,
  useSearchParams,
} from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { formatStorefrontDate } from "~/utils/dates";
import { detectLocale, localePath } from "~/utils/locale";
import { t } from "~/utils/translations";
import { validateEmail } from "~/utils/validation";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";
import { formatStorefrontPrice } from "~/utils/money";
import { formatOrderNumber } from "~/utils/orderNumber";
import prisma from "~/utils/db.server";
import { checkRateLimit, getClientIp } from "~/utils/rateLimiter.server";
import { interpretVtpStatus } from "~/services/shipping/viettelpostStatusMap.server";

// ─── Bilingual labels ─── //

function txt(key: string, locale: Locale): string {
  const map: Record<string, Record<Locale, string>> = {
    pageTitle: { en: "Track Your Order", vi: "Theo dõi đơn hàng" },
    pageSubtitle: {
      en: "Enter your order number and email address to check your order status.",
      vi: "Nhập mã đơn hàng và email để kiểm tra tình trạng đơn hàng của bạn.",
    },
    orderNumber: { en: "Order Number", vi: "Mã đơn hàng" },
    emailAddress: { en: "Email Address", vi: "Địa chỉ email" },
    trackOrder: { en: "Track Order", vi: "Theo dõi" },
    tracking: { en: "Tracking", vi: "Theo dõi" },
    searching: { en: "Searching...", vi: "Đang tìm..." },
    orderNotFound: { en: "Order not found", vi: "Không tìm thấy đơn hàng" },
    orderNotFoundDesc: {
      en: "We couldn't find an order matching that order number and email. Please double-check and try again.",
      vi: "Chúng tôi không tìm thấy đơn hàng khớp với mã đơn và email. Vui lòng kiểm tra lại.",
    },
    orderNumberRequired: { en: "Order number is required", vi: "Vui lòng nhập mã đơn hàng" },
    emailRequired: { en: "Email is required", vi: "Vui lòng nhập email" },
    invalidEmail: { en: "Invalid email address", vi: "Email không hợp lệ" },
    orderStatus: { en: "Order Status", vi: "Trạng thái đơn hàng" },
    orderDate: { en: "Order Date", vi: "Ngày đặt" },
    orderSummary: { en: "Order Summary", vi: "Tóm tắt đơn hàng" },
    trackingInfo: { en: "Tracking Information", vi: "Thông tin vận chuyển" },
    trackingNumber: { en: "Tracking Number", vi: "Mã vận đơn" },
    trackShipment: { en: "Track Shipment", vi: "Theo dõi vận chuyển" },
    carrierUpdates: { en: "Carrier updates", vi: "Hành trình đơn hàng" },
    returnedNote: { en: "The parcel was returned to us. We'll be in touch about a redelivery or refund.", vi: "Kiện hàng đã hoàn về kho. Chúng tôi sẽ liên hệ để giao lại hoặc hoàn tiền." },
    product: { en: "Product", vi: "Sản phẩm" },
    variant: { en: "Variant", vi: "Phân loại" },
    qty: { en: "Qty", vi: "SL" },
    price: { en: "Price", vi: "Giá" },
    subtotal: { en: "Subtotal", vi: "Tạm tính" },
    shipping: { en: "Shipping", vi: "Phí vận chuyển" },
    tax: { en: "Tax", vi: "Thuế" },
    discount: { en: "Discount", vi: "Giảm giá" },
    total: { en: "Total", vi: "Tổng cộng" },
    searchAnother: { en: "Track another order", vi: "Tra cứu đơn hàng khác" },
    // Status steps
    stepPending: { en: "Confirmed", vi: "Đã xác nhận" },
    stepProcessing: { en: "Processing", vi: "Đang xử lý" },
    stepShipped: { en: "Shipped", vi: "Đang giao" },
    stepDelivered: { en: "Delivered", vi: "Đã giao" },
    // Cancelled / Refunded
    orderCancelled: { en: "This order has been cancelled.", vi: "Đơn hàng này đã bị hủy." },
    orderRefunded: { en: "This order has been refunded.", vi: "Đơn hàng này đã được hoàn tiền." },
    // Payment
    paymentMethod: { en: "Payment", vi: "Thanh toán" },
    paymentPending: { en: "Pending", vi: "Chờ thanh toán" },
    paymentSucceeded: { en: "Paid", vi: "Đã thanh toán" },
    paymentFailed: { en: "Failed", vi: "Thất bại" },
    paymentProcessing: { en: "Processing", vi: "Đang xử lý" },
    paymentRefunded: { en: "Refunded", vi: "Đã hoàn tiền" },
  };
  return map[key]?.[locale] ?? key;
}

// ─── Meta ─── //

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = (data as { locale?: Locale } | undefined)?.locale ?? "vi";
  return [
    {
      title:
        locale === "vi"
          ? "Theo dõi đơn hàng — Diolo"
          : "Order Tracking — Diolo",
    },
    {
      name: "description",
      content:
        locale === "vi"
          ? "Tra cứu trạng thái đơn hàng Diolo bằng mã đơn và email."
          : "Track your Diolo order status with your order number and email.",
    },
  ];
};

// ─── Loader ─── //

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json({ locale: detectLocale(request.url) });
};

// ─── Action: look up order ─── //

interface ActionData {
  errors?: { orderNumber?: string; email?: string };
  notFound?: boolean;
  order?: {
    id: string;
    orderNumber: string;
    status: string;
    email: string;
    subtotal: number;
    shippingTotal: number;
    taxTotal: number;
    discountCode: string | null;
    discountAmount: number;
    grandTotal: number;
    currency: string;
    paymentMethod: string | null;
    paymentStatus: string;
    trackingNumber: string | null;
    trackingUrl: string | null;
    /** Carrier scans, newest first (Viettel Post webhook / reconciliation). */
    events: { at: string; label: string; location: string | null }[];
    createdAt: string;
    items: {
      id: string;
      productTitle: string;
      variantTitle: string | null;
      sku: string | null;
      quantity: number;
      unitPrice: number;
    }[];
  };
}

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const locale = (formData.get("locale") as Locale) || detectLocale(request.url);
  const orderNumber = (formData.get("orderNumber") as string)?.trim() || "";
  const email = (formData.get("email") as string)?.trim().toLowerCase() || "";

  // Validate inputs
  const errors: ActionData["errors"] = {};
  if (!orderNumber) errors.orderNumber = txt("orderNumberRequired", locale);
  if (!email) errors.email = txt("emailRequired", locale);
  else if (!validateEmail(email))
    errors.email = txt("invalidEmail", locale);

  if (Object.keys(errors).length > 0) {
    return json<ActionData>({ errors }, { status: 400 });
  }

  // Audit 2026-04-30: rate-limit order-tracking lookups by IP. The
  // email-match in the WHERE clause already requires an attacker to
  // know the customer's email, but brute-forcing orderNumbers (which
  // are sequential RO-XXXXX) for a known email is the next escalation.
  // 20 lookups per IP per 15 min covers the legit "I lost my order
  // number" use case without enabling enumeration.
  const ip = getClientIp(request);
  const { allowed, retryAfterMs } = await checkRateLimit(
    `order-tracking:${ip}`,
    20,
    15 * 60 * 1000,
  );
  if (!allowed) {
    return json<ActionData>(
      { errors: { orderNumber: txt("genericError", locale) || "Too many lookups. Please try again later." } },
      {
        status: 429,
        headers: { "Retry-After": String(Math.ceil(retryAfterMs / 1000)) },
      },
    );
  }

  // Look up order by orderNumber + email (email match is required server-side)
  const order = await prisma.retailOrder.findFirst({
    where: {
      orderNumber: orderNumber,
      email: { equals: email, mode: "insensitive" },
    },
    include: {
      items: true,
      trackingEvents: {
        orderBy: { occurredAt: "desc" },
        take: 8,
        select: { occurredAt: true, vtpStatusName: true, vtpStatusCode: true, vtpLocation: true },
      },
    },
  });

  if (!order) {
    return json<ActionData>({ notFound: true }, { status: 404 });
  }

  return json<ActionData>({
    order: {
      id: order.id,
      orderNumber: order.orderNumber,
      status: order.status,
      email: order.email,
      subtotal: Number(order.subtotal),
      shippingTotal: Number(order.shippingTotal),
      taxTotal: Number(order.taxTotal),
      discountCode: order.discountCode,
      discountAmount: Number(order.discountAmount),
      grandTotal: Number(order.grandTotal),
      currency: order.currency,
      paymentMethod: order.paymentMethod,
      paymentStatus: order.paymentStatus,
      trackingNumber: order.trackingNumber,
      trackingUrl: order.trackingUrl,
      events: order.trackingEvents.map((ev) => ({
        at: ev.occurredAt.toISOString(),
        // Customer-facing: the carrier's own Vietnamese wording, English label fallback.
        label: ev.vtpStatusName || (ev.vtpStatusCode != null ? interpretVtpStatus(ev.vtpStatusCode).label : ""),
        location: ev.vtpLocation,
      })),
      createdAt: order.createdAt.toISOString(),
      items: order.items.map((item) => ({
        id: item.id,
        productTitle: item.productTitle,
        variantTitle: item.variantTitle,
        sku: item.sku,
        quantity: item.quantity,
        unitPrice: Number(item.unitPrice),
      })),
    },
  });
};

// ─── Status Stepper ─── //

const TIMELINE_STEPS = ["PENDING", "PROCESSING", "SHIPPED", "DELIVERED"] as const;

function StatusTimeline({
  status,
  locale,
}: {
  status: string;
  locale: Locale;
}) {
  const isCancelled = status === "CANCELLED" || status === "REFUNDED";

  if (isCancelled) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200">
        <svg
          className="w-5 h-5 text-red-500 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        <span className="text-sm font-medium text-red-700">
          {status === "CANCELLED"
            ? txt("orderCancelled", locale)
            : txt("orderRefunded", locale)}
        </span>
      </div>
    );
  }

  const stepLabels: Record<string, (loc: Locale) => string> = {
    PENDING: (loc) => txt("stepPending", loc),
    PROCESSING: (loc) => txt("stepProcessing", loc),
    SHIPPED: (loc) => txt("stepShipped", loc),
    DELIVERED: (loc) => txt("stepDelivered", loc),
  };

  const stepIcons = {
    PENDING: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    ),
    PROCESSING: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93s.844.112 1.207-.108l.738-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.22.363-.272.804-.108 1.207.164.397.505.71.93.78l.893.15c.543.09.94.56.94 1.11v1.093c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.766.383-.93.78-.165.398-.112.844.107 1.207l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.363-.22-.804-.272-1.207-.108-.396.164-.71.505-.78.93l-.15.894c-.09.542-.56.94-1.11.94h-1.093c-.55 0-1.02-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.844-.112-1.207.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.22-.363.272-.804.108-1.207a2.5 2.5 0 00-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.11v-1.093c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.164-.398.112-.844-.108-1.207l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.363.22.804.272 1.207.108.397-.165.71-.505.78-.93l.15-.894z"
      />
    ),
    SHIPPED: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
      />
    ),
    DELIVERED: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
      />
    ),
  };

  // Map PAID status to the same step as PENDING (Confirmed)
  // RETURNED sits on the Shipped step (the parcel did leave) with a note below.
  const normalizedStatus = status === "PAID" ? "PENDING" : status === "RETURNED" ? "SHIPPED" : status;
  const currentIdx = TIMELINE_STEPS.indexOf(
    normalizedStatus as (typeof TIMELINE_STEPS)[number]
  );
  const activeIdx = currentIdx >= 0 ? currentIdx : 0;

  return (
    <div className="relative">
      {/* Desktop timeline */}
      <div className="hidden sm:flex items-start justify-between">
        {TIMELINE_STEPS.map((step, i) => {
          const isComplete = activeIdx >= i;
          const isCurrent = activeIdx === i;
          return (
            <div
              key={step}
              className="flex flex-col items-center flex-1 last:flex-none relative"
            >
              {/* Connector line */}
              {i < TIMELINE_STEPS.length - 1 && (
                <div
                  className={`absolute top-5 left-1/2 w-full h-[2px] ${
                    activeIdx > i ? "bg-luxury-black" : "bg-luxury-sand"
                  }`}
                />
              )}
              {/* Step circle */}
              <div
                className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  isComplete
                    ? "bg-luxury-black text-white"
                    : "bg-white border-2 border-luxury-sand text-luxury-stone"
                } ${isCurrent ? "ring-2 ring-luxury-black/20 ring-offset-2" : ""}`}
              >
                {isComplete ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    {stepIcons[step]}
                  </svg>
                )}
              </div>
              <span
                className={`text-[10px] sm:text-xs uppercase tracking-wider mt-2 text-center leading-tight ${
                  isComplete
                    ? "text-luxury-charcoal font-medium"
                    : "text-luxury-stone"
                }`}
              >
                {stepLabels[step](locale)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Mobile timeline (vertical) */}
      <div className="sm:hidden space-y-0">
        {TIMELINE_STEPS.map((step, i) => {
          const isComplete = activeIdx >= i;
          const isCurrent = activeIdx === i;
          return (
            <div key={step} className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isComplete
                      ? "bg-luxury-black text-white"
                      : "bg-white border-2 border-luxury-sand text-luxury-stone"
                  } ${isCurrent ? "ring-2 ring-luxury-black/20 ring-offset-2" : ""}`}
                >
                  {isComplete ? (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                  ) : (
                    <span className="text-[10px] font-medium">{i + 1}</span>
                  )}
                </div>
                {i < TIMELINE_STEPS.length - 1 && (
                  <div
                    className={`w-[2px] h-8 ${
                      activeIdx > i ? "bg-luxury-black" : "bg-luxury-sand"
                    }`}
                  />
                )}
              </div>
              <div className="pt-1 pb-4">
                <span
                  className={`text-xs uppercase tracking-wider ${
                    isComplete
                      ? "text-luxury-charcoal font-medium"
                      : "text-luxury-stone"
                  }`}
                >
                  {stepLabels[step](locale)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Page Component ─── //

export default function OrderTrackingPage() {
  const { locale: outletLocale } = useOutletContext<{ locale: Locale }>();
  const locale = outletLocale ?? "vi";
  const actionData = useActionData<ActionData>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const [searchParams] = useSearchParams();

  const order = actionData?.order;
  // Pre-fill from `?orderNumber=…&email=…` when arriving from the checkout
  // confirmation page. Pre-fill only — we don't auto-submit because the URL
  // could be shared and the customer should consciously trigger the lookup.
  const prefillOrderNumber = searchParams.get("orderNumber") || "";
  const prefillEmail = searchParams.get("email") || "";
  // When the customer landed here from the Returns page CTA, surface a
  // banner explaining the flow so they don't think they're in the wrong place.
  const startingReturn = searchParams.get("startReturn") === "1";

  return (
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-12">
      <Breadcrumbs
        locale={locale}
        items={[{ label: txt("tracking", locale) }]}
      />

      {/* Header */}
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
          {txt("tracking", locale)}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-luxury-charcoal tracking-normal">
          {txt("pageTitle", locale)}
        </h1>
        <p className="mt-4 max-w-xl mx-auto text-luxury-stone text-sm leading-relaxed">
          {txt("pageSubtitle", locale)}
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Return / exchange entry banner — shown only when the customer
            arrived from the Returns page CTA (?startReturn=1). */}
        {startingReturn && !order && (
          <div className="mb-6 p-4 bg-luxury-cream border border-luxury-sand text-center">
            <p className="text-sm text-luxury-charcoal">
              {locale === "vi"
                ? "Để bắt đầu đổi trả, vui lòng tra cứu đơn hàng của bạn dưới đây."
                : "To start a return or exchange, look up your order below."}
            </p>
          </div>
        )}

        {/* ─── Lookup Form ─── */}
        {!order && (
          <div className="bg-white border border-luxury-sand p-6 sm:p-8">
            {actionData?.notFound && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200">
                <p className="text-sm font-medium text-red-700">
                  {txt("orderNotFound", locale)}
                </p>
                <p className="text-sm text-red-600 mt-1">
                  {txt("orderNotFoundDesc", locale)}
                </p>
              </div>
            )}

            <Form method="post" className="space-y-5" noValidate>
              <input type="hidden" name="locale" value={locale} />
              <div>
                <label className="block text-xs uppercase tracking-widest text-luxury-stone mb-2">
                  {txt("orderNumber", locale)}
                </label>
                <input
                  type="text"
                  name="orderNumber"
                  required
                  defaultValue={prefillOrderNumber}
                  className="sf-input w-full"
                  placeholder={locale === "vi" ? "VD: Diolo-100001" : "e.g. Diolo-100001"}
                  aria-invalid={
                    actionData?.errors?.orderNumber ? true : undefined
                  }
                />
                {actionData?.errors?.orderNumber && (
                  <p className="mt-1 text-xs text-red-600">
                    {actionData.errors.orderNumber}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-luxury-stone mb-2">
                  {txt("emailAddress", locale)}
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  defaultValue={prefillEmail}
                  className="sf-input w-full"
                  placeholder="you@example.com"
                  aria-invalid={actionData?.errors?.email ? true : undefined}
                />
                {actionData?.errors?.email && (
                  <p className="mt-1 text-xs text-red-600">
                    {actionData.errors.email}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="sf-btn-filled w-full disabled:opacity-50"
              >
                {isSubmitting
                  ? txt("searching", locale)
                  : txt("trackOrder", locale)}
              </button>
            </Form>
          </div>
        )}

        {/* ─── Order Result ─── */}
        {order && (
          <div className="space-y-6">
            {/* Back / search again */}
            <Form method="get">
              <button
                type="submit"
                className="inline-block text-sm text-luxury-stone hover:text-luxury-charcoal transition-colors"
              >
                &larr; {txt("searchAnother", locale)}
              </button>
            </Form>

            {/* Order Header */}
            <div className="bg-white border border-luxury-sand p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                  <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal">
                    {formatOrderNumber(order.orderNumber)}
                  </h2>
                  <p className="text-sm text-luxury-stone mt-1">
                    {txt("orderDate", locale)}:{" "}
                    {formatStorefrontDate(order.createdAt, locale, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-3 self-start">
                  {order.paymentStatus && (
                    <span className="inline-block px-3 py-1 text-xs font-medium rounded bg-luxury-cream text-luxury-charcoal">
                      {txt(
                        `payment${order.paymentStatus.charAt(0)}${order.paymentStatus.slice(1).toLowerCase()}`,
                        locale
                      )}
                    </span>
                  )}
                </div>
              </div>

              {/* Status Timeline */}
              <StatusTimeline status={order.status} locale={locale} />
            </div>

            {/* Tracking Info */}
            {order.trackingNumber && (
              <div className="bg-white border border-luxury-sand p-6 sm:p-8">
                <h3 className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
                  {txt("trackingInfo", locale)}
                </h3>
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-luxury-stone flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
                    />
                  </svg>
                  <div>
                    <p className="text-xs text-luxury-stone mb-1">
                      {txt("trackingNumber", locale)}
                    </p>
                    {order.trackingUrl ? (
                      <a
                        href={order.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-luxury-charcoal underline hover:text-luxury-black"
                      >
                        {order.trackingNumber}
                      </a>
                    ) : (
                      <span className="text-sm font-medium text-luxury-charcoal">
                        {order.trackingNumber}
                      </span>
                    )}
                  </div>
                  {order.trackingUrl && (
                    <a
                      href={order.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto sf-btn-secondary text-sm"
                    >
                      {txt("trackShipment", locale)} &rarr;
                    </a>
                  )}
                </div>
                {order.status === "RETURNED" && (
                  <p className="mt-4 text-sm text-luxury-charcoal bg-luxury-cream px-4 py-3">{txt("returnedNote", locale)}</p>
                )}
                {order.events.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs uppercase tracking-widest text-luxury-stone mb-3">{txt("carrierUpdates", locale)}</p>
                    <ol className="space-y-2">
                      {order.events.map((ev, i) => (
                        <li key={i} className="flex gap-3 text-sm">
                          <span className="w-28 shrink-0 text-luxury-stone tabular-nums" suppressHydrationWarning>
                            {new Date(ev.at).toLocaleString(locale === "vi" ? "vi-VN" : "en-GB", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Ho_Chi_Minh" })}
                          </span>
                          <span className="text-luxury-charcoal">
                            {ev.label}
                            {ev.location ? <span className="text-luxury-stone"> · {ev.location}</span> : null}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}

            {/* Order Items */}
            <div className="bg-white border border-luxury-sand overflow-hidden">
              <div className="px-6 py-4 border-b border-luxury-sand">
                <h3 className="text-xs uppercase tracking-widest text-luxury-stone">
                  {txt("orderSummary", locale)}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-luxury-sand bg-luxury-cream">
                      <th className="text-left text-xs uppercase tracking-widest text-luxury-stone px-4 py-3">
                        {txt("product", locale)}
                      </th>
                      <th className="text-left text-xs uppercase tracking-widest text-luxury-stone px-4 py-3 hidden sm:table-cell">
                        {txt("variant", locale)}
                      </th>
                      <th className="text-right text-xs uppercase tracking-widest text-luxury-stone px-4 py-3">
                        {txt("qty", locale)}
                      </th>
                      <th className="text-right text-xs uppercase tracking-widest text-luxury-stone px-4 py-3">
                        {txt("price", locale)}
                      </th>
                      <th className="text-right text-xs uppercase tracking-widest text-luxury-stone px-4 py-3">
                        {txt("subtotal", locale)}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b border-luxury-sand last:border-0"
                      >
                        <td className="px-4 py-3 text-luxury-charcoal">
                          {item.productTitle}
                          {/* Mobile: show variant inline */}
                          {item.variantTitle && (
                            <span className="block sm:hidden text-xs text-luxury-stone mt-0.5">
                              {item.variantTitle}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-luxury-stone hidden sm:table-cell">
                          {item.variantTitle ?? "-"}
                        </td>
                        <td className="px-4 py-3 text-right text-luxury-charcoal">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-right text-luxury-charcoal">
                          {formatStorefrontPrice(item.unitPrice, order.currency)}
                        </td>
                        <td className="px-4 py-3 text-right text-luxury-charcoal">
                          {formatStorefrontPrice(
                            item.unitPrice * item.quantity,
                            order.currency
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="border-t border-luxury-sand p-6">
                <div className="max-w-xs ml-auto space-y-2 text-sm">
                  <div className="flex justify-between text-luxury-stone">
                    <span>{txt("subtotal", locale)}</span>
                    <span>
                      {formatStorefrontPrice(order.subtotal, order.currency)}
                    </span>
                  </div>
                  {order.discountAmount > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>
                        {txt("discount", locale)}
                        {order.discountCode ? ` (${order.discountCode})` : ""}
                      </span>
                      <span>
                        -
                        {formatStorefrontPrice(
                          order.discountAmount,
                          order.currency
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-luxury-stone">
                    <span>{txt("shipping", locale)}</span>
                    <span>
                      {formatStorefrontPrice(
                        order.shippingTotal,
                        order.currency
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-luxury-stone">
                    <span>{txt("tax", locale)}</span>
                    <span>
                      {formatStorefrontPrice(order.taxTotal, order.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between font-medium text-luxury-charcoal border-t border-luxury-sand pt-2 mt-2">
                    <span>{txt("total", locale)}</span>
                    <span>
                      {formatStorefrontPrice(order.grandTotal, order.currency)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Return / Exchange CTA — only shown for delivered orders.
                Self-serve return submission requires sign-in for security
                (preventing return fraud on guest orders), but the lookup
                + the policy info above are fully guest-accessible. */}
            {order.status === "DELIVERED" && (
              <div className="bg-white border border-luxury-sand p-6 sm:p-8">
                <h3 className="font-display text-lg font-light text-luxury-charcoal mb-2">
                  {locale === "vi"
                    ? "Đổi trả sản phẩm"
                    : "Return or Exchange"}
                </h3>
                <p className="text-sm text-luxury-stone mb-5">
                  {locale === "vi"
                    ? "Đăng nhập để gửi yêu cầu đổi trả tự động, hoặc liên hệ với chúng tôi và nhân viên hỗ trợ sẽ xử lý cho bạn."
                    : "Sign in to submit a return request automatically, or contact us and our team will help you complete it."}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={localePath(`/account/login?redirectTo=/account/orders/${order.id}/return`, locale)}
                    className="sf-btn-filled text-xs"
                  >
                    {locale === "vi" ? "Đăng nhập để đổi trả" : "Sign In to Start Return"}
                  </Link>
                  <a
                    href={`mailto:hello@diolo.com?subject=${encodeURIComponent(
                      (locale === "vi" ? "Yêu cầu đổi trả - " : "Return request - ") + order.orderNumber,
                    )}&body=${encodeURIComponent(
                      (locale === "vi"
                        ? `Xin chào,\n\nTôi muốn yêu cầu đổi trả cho đơn hàng ${order.orderNumber}.\n\nLý do:\nSản phẩm:\n\nCảm ơn,`
                        : `Hi,\n\nI'd like to request a return / exchange for order ${order.orderNumber}.\n\nReason:\nItem(s):\n\nThanks,`)
                    )}`}
                    className="sf-btn-secondary text-xs"
                  >
                    {locale === "vi" ? "Liên hệ hỗ trợ" : "Contact Support"}
                  </a>
                </div>
              </div>
            )}

            {/* Search again form at bottom */}
            <div className="bg-luxury-cream p-6 sm:p-8 text-center">
              <p className="text-sm text-luxury-stone mb-4">
                {locale === "vi"
                  ? "Muốn tra cứu đơn hàng khác?"
                  : "Need to look up another order?"}
              </p>
              <Form method="get">
                <button type="submit" className="sf-btn-secondary text-sm">
                  {txt("searchAnother", locale)}
                </button>
              </Form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  return (
    <div className="mx-auto max-w-md px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-light text-luxury-charcoal mb-4">
        Something went wrong
      </h1>
      <p className="text-luxury-stone text-sm mb-8">Please try again.</p>
      <a href="/" className="sf-btn-primary inline-block">
        Return Home
      </a>
    </div>
  );
}
